import { makeAutoObservable, runInAction } from "mobx";
import RootStore from "stores/RootStore";
import ServiceStore from "stores/ServiceStore";

interface CylinderRecord {
  id?: number;
  cylinderNumber: string;
  batchNumber: string;
  deliveryTicketId: number | null;
  jobId: number | null;

  // Collection Details
  collectionDate: Date;
  collectionTime: string;
  collectedBy: number | null;
  collectionLocation: string;

  // Mix Information
  mixDesignId: number | null;
  mixDesignName: string;
  strengthRequirement: string;
  ageAtTest: number; // days (7, 14, 28)

  // Testing
  testDate: Date | null;
  testLab: string;
  testedBy: string;
  actualStrength: number | null;
  passedSpec: boolean;

  // Curing
  curingMethod: string;
  curingLocation: string;
  curingTemperature: number | null;

  // Status
  status: "collected" | "curing" | "ready_for_test" | "tested" | "failed";
  notes: string;
}

class CylinderTrackingWorkflowStore {
  rootStore: RootStore;

  loading = false;
  error: string | null = null;
  success: string | null = null;
  activeStep = 0;
  cylinderData: CylinderRecord;
  activeCylinders: any[] = [];

  constructor(rootStore: RootStore) {
    this.cylinderData = this.getDefaultCylinder();
    this.rootStore = rootStore;
    makeAutoObservable(this, { rootStore: false });
  }

  getDefaultCylinder = (): CylinderRecord => ({
    cylinderNumber: this.generateCylinderNumber(),
    batchNumber: "",
    deliveryTicketId: null,
    jobId: null,

    collectionDate: new Date(),
    collectionTime: new Date().toLocaleTimeString("en-US", { hour12: false }),
    collectedBy: null,
    collectionLocation: "",

    mixDesignId: null,
    mixDesignName: "",
    strengthRequirement: "4000 PSI",
    ageAtTest: 28,

    testDate: null,
    testLab: "",
    testedBy: "",
    actualStrength: null,
    passedSpec: true,

    curingMethod: "Moist room",
    curingLocation: "",
    curingTemperature: null,

    status: "collected",
    notes: "",
  });

  generateCylinderNumber = (): string => {
    const now = new Date();
    const year = now.getFullYear().toString().slice(-2);
    const month = (now.getMonth() + 1).toString().padStart(2, "0");
    const day = now.getDate().toString().padStart(2, "0");
    const random = Math.floor(Math.random() * 1000).toString().padStart(3, "0");
    return `CYL${year}${month}${day}-${random}`;
  };

  setLoading = (loading: boolean) => {
    this.loading = loading;
  };

  setError = (error: string | null) => {
    this.error = error;
  };

  setSuccess = (success: string | null) => {
    this.success = success;
  };

  setActiveStep = (step: number) => {
    this.activeStep = step;
  };

  nextStep = () => {
    if (this.validateCurrentStep()) {
      this.activeStep++;
    }
  };

  prevStep = () => {
    this.activeStep--;
  };

  updateCylinderData = <K extends keyof CylinderRecord>(
    field: K,
    value: CylinderRecord[K]
  ) => {
    this.cylinderData[field] = value;
  };

  validateCurrentStep = (): boolean => {
    this.setError(null);

    switch (this.activeStep) {
      case 0:
        if (!this.cylinderData.batchNumber) {
          this.setError("Batch number is required");
          return false;
        }
        if (!this.cylinderData.collectedBy) {
          this.setError("Please specify who collected the cylinder");
          return false;
        }
        break;
    }

    return true;
  };

  loadInitialData = async () => {
    this.setLoading(true);
    try {
      const userStore = this.rootStore.userStore as ServiceStore;
      await userStore?.find();
    } catch (err: any) {
      runInAction(() => this.setError("Failed to load data"));
    } finally {
      runInAction(() => this.setLoading(false));
    }
  };

  submitCylinder = async () => {
    if (!this.validateCurrentStep()) return;

    this.setLoading(true);
    try {
      // TODO: Submit cylinder record when service is available
      runInAction(() => {
        this.setSuccess("Cylinder record created successfully!");
        this.activeStep = 2;
      });
    } catch (err: any) {
      runInAction(() => this.setError(err.message || "Failed to create cylinder record"));
    } finally {
      runInAction(() => this.setLoading(false));
    }
  };

  reset = () => {
    this.loading = false;
    this.error = null;
    this.success = null;
    this.activeStep = 0;
    this.cylinderData = this.getDefaultCylinder();
  };

  get users() {
    const userStore = this.rootStore.userStore as ServiceStore;
    return userStore?.entities || [];
  }

  get expectedTestDate() {
    if (!this.cylinderData.collectionDate) return null;
    const testDate = new Date(this.cylinderData.collectionDate);
    testDate.setDate(testDate.getDate() + this.cylinderData.ageAtTest);
    return testDate;
  }
}

export default CylinderTrackingWorkflowStore;
